import React from "react";
import { useHistory } from "react-router-dom";
import { DARKER_TEXT, WARNING_ORANGE, ERROR_RED, SUCCESS_GREEN } from "resources/constants/colors";
import { IMAGES_URL } from "resources/constants/urls";
import styled, { keyframes } from "styled-components";
import useWellness from 'state/wellness';

// Paleta de colores para avatares de iniciales
const AVATAR_COLORS = [
    ['#6C63FF', '#fff'],  // púrpura / blanco
    ['#26C6DA', '#fff'],  // cyan / blanco
    ['#66BB6A', '#fff'],  // verde suave / blanco
    ['#FFA726', '#fff'],  // naranja / blanco
    ['#EF5350', '#fff'],  // rojo coral / blanco
    ['#AB47BC', '#fff'],  // violeta / blanco
    ['#42A5F5', '#fff'],  // azul claro / blanco
    ['#8D6E63', '#fff'],  // café / blanco
];

function getAvatarColor(name = '') {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name = '', lastName = '') {
    const a = name.trim()[0] || '';
    const b = lastName.trim()[0] || '';
    return (a + b).toUpperCase();
}

const pulse = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(231,76,60,0.4); }
    50%       { box-shadow: 0 0 0 5px rgba(231,76,60,0); }
`;

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 4px;
    cursor: pointer;
    border-bottom: 1px solid #f2f2f2;
    transition: opacity 0.15s;
    &:last-child { border-bottom: none; }
    &:active { opacity: 0.65; }
`;

const AvatarWrap = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
    position: relative;
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const InitialsAvatar = styled.div`
    width: 100%;
    height: 100%;
    background: ${({ bg }) => bg};
    color: ${({ fg }) => fg};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.5px;
    user-select: none;
`;

const StatusDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    position: absolute;
    bottom: 1px;
    right: 1px;
    border: 2px solid #fff;
    background: ${({ color }) => color};
    animation: ${({ sos }) => sos ? pulse : 'none'} 1.4s ease infinite;
`;

const Info = styled.div`
    flex: 1;
    min-width: 0;
`;

const Name = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AlertLabel = styled.span`
    font-size: 11px;
    color: ${({ color }) => color};
    font-weight: 600;
`;

const Arrow = styled.span`
    font-size: 18px;
    color: #ccc;
    flex-shrink: 0;
`;

const PatientCard = ({ person }) => {
    const [wellness] = useWellness();
    const { alerts } = wellness;
    const history = useHistory();

    const hasSos = alerts.sosAlerts?.some(a => a.clientId === person.id);
    const hasMoodAlert = alerts.moodAlerts?.some(a => a.clientId === person.id);

    const statusColor = hasSos ? ERROR_RED : hasMoodAlert ? WARNING_ORANGE : SUCCESS_GREEN;
    const statusLabel = hasSos ? '🆘 SOS reciente' : hasMoodAlert ? '📉 Mood bajo' : null;

    const [bg, fg] = getAvatarColor(`${person.name}${person.lastName}`);
    const initials = getInitials(person.name, person.lastName);

    return (
        <Container onClick={() => history.push(`/home/expediente/${person.id}`)}>
            <AvatarWrap>
                {person.profileImg
                    ? <img src={`${IMAGES_URL}${person.profileImg}`} alt={`perfil de ${person.name}`} />
                    : <InitialsAvatar bg={bg} fg={fg}>{initials}</InitialsAvatar>
                }
                <StatusDot color={statusColor} sos={hasSos ? 1 : 0} />
            </AvatarWrap>
            <Info>
                <Name>{`${person.name} ${person.lastName}`}</Name>
                {statusLabel && <AlertLabel color={statusColor}>{statusLabel}</AlertLabel>}
            </Info>
            <Arrow>›</Arrow>
        </Container>
    );
};

export default PatientCard;

